import React, {useEffect, useRef, useState} from 'react'
import {useIntl, defineMessages} from 'react-intl'
import PropTypes from 'prop-types'
import {nanoid} from 'nanoid'

import {
    Box,
    Button,
    Heading,
    Stack,
    Text,
    Flex,
    Alert,
    IconButton,

    // Hooks
    useTheme,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

/* Hooks */
import {useHistory, useLocation} from 'react-router-dom'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useCustomToast} from '../../contexts/custom-toast-context'

/* Icons */
import {LeftArrow, EditPencil} from '../../components/icons'

/* Forms */
import {useForm} from 'react-hook-form'
import AddressBookFields from '../../components/forms/address-book-fields'

/* Components */
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {sendViewErrorEvent} from '../../utils/gtm-utils'

/* Constants and static resources */
const staticResources = defineMessages({
    addressHeading: {
        id: 'account.address_book.address_heading',
        defaultMessage: 'Address Book'
    },
    newAddressHeading: {
        id: 'account.address_book.new_address_heading',
        defaultMessage: 'New Address'
    },
    editAddressHeading: {
        id: 'account.address_book.edit_address_heading',
        defaultMessage: 'Update Address'
    },
    addAddressLabel: {
        id: 'account.address_book.add_address',
        defaultMessage: 'Add Address'
    },
    updatedAddressLabel: {
        id: 'account.address_book.update_address',
        defaultMessage: 'Update'
    },
    deleteAddressLabel: {
        id: 'account.address_book.delete_address',
        defaultMessage: 'Delete this address'
    },
    noAddressTitle: {
        id: 'account.address_book.no_address_title',
        defaultMessage: 'No Saved Addresses'
    },
    noAddressSubTitle: {
        id: 'account.address_book.no_address_subtitle',
        defaultMessage: 'Click below to add an address.'
    },
    successMessage: {
        id: 'account.address_book.success_message',
        defaultMessage: 'The changes have been saved'
    },
    errorMessage: {
        id: 'account.address_book.error_message',
        defaultMessage: 'Sorry, something went wrong. Please try again.'
    },
    preferred: {
        id: 'account.address_book.default_address',
        defaultMessage: 'Default delivery address'
    },
    ukCountryName: {
        id: 'account.address_book.uk_country_name',
        defaultMessage: 'United Kingdom'
    }
})

const addressFormResetObject = {
    addressTitle: '',
    address1: '',
    apartment: '',
    city: '',
    countryCode: 'GB',
    firstName: '',
    lastName: '',
    phone: '',
    postalCode: '',
    stateCode: '',
    preferred: false,
    email: ''
}

const MANDATORY_FIELDS = [
    'firstName',
    'lastName',
    'address1',
    'city',
    'postalCode',
    'countryCode',
    'phone'
]

/* Helper functions */
const getAddressModeFromPath = (search) => {
    const params = new URLSearchParams(search)
    return params.get('mode') || null
}

const getDefaultMode = (addresses) => {
    return addresses?.length > 0 ? 'savedaddress' : 'noaddress'
}

/**
 * HeadingAddressBook is a functional React component that renders the heading section for the address book page in a user's account area.
 * It dynamically displays different headings and back navigation buttons based on the current address mode, such as viewing, adding, editing, or having no addresses.
 * The component adapts its layout and navigation controls for mobile and desktop views, utilizing theme-based styling and internationalized messages.
 *
 * Props:
 * @param {string} addressMode - Determines the current state of the address book ('noaddress', 'addaddress', 'savedaddress', 'editaddress', or 'unavailable').
 * @param {function} backFromAddAddress - Callback function to handle navigation when returning from the add address form.
 * @param {function} backFromEditAddress - Callback function to handle navigation when returning from the edit address form.
 *
 * Features:
 * - Renders context-appropriate headings and back buttons based on the addressMode prop.
 * - Utilizes responsive design to show back navigation only on mobile devices.
 * - Integrates with the application's theme and internationalization system for consistent styling and messaging.
 * - Employs React Router's history for navigation and Chakra UI's theming and layout utilities.
 */
const HeadingAddressBook = ({addressMode, backFromAddAddress, backFromEditAddress}) => {
    const theme = useTheme()
    const {baseStyle, headingAddressBook} = theme.components.AddressBook
    const intl = useIntl()
    const isMobile = useBreakpointValue({base: true, md: false})
    const history = useHistory()

    return (
        <Heading
            {...headingAddressBook.container}

            {...baseStyle.centerContent}
            as="h2"
            tabIndex="0"
        >
            {/* No address heading */}
            {addressMode === 'noaddress' && addressMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            variant="link"
                            {...headingAddressBook.backButton}
                            onClick={() => history.push('/account')}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    {intl.formatMessage(staticResources.addressHeading)}
                </React.Fragment>
            )}

            {/* Add address heading */}
            {addressMode === 'addaddress' && addressMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            variant="link"
                            {...headingAddressBook.backButton}
                            onClick={backFromAddAddress}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    {intl.formatMessage(staticResources.newAddressHeading)}
                </React.Fragment>
            )}

            {/* Saved address heading */}
            {addressMode === 'savedaddress' && addressMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            variant="link"
                            {...headingAddressBook.backButton}
                            onClick={() => history.push('/account')}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    {intl.formatMessage(staticResources.addressHeading)}
                </React.Fragment>
            )}

            {/* Edit address heading */}
            {addressMode === 'editaddress' && addressMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            variant="link"
                            {...headingAddressBook.backButton}
                            onClick={backFromEditAddress}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    {intl.formatMessage(staticResources.editAddressHeading)}
                </React.Fragment>
            )}
        </Heading>
    )
}

HeadingAddressBook.propTypes = {
    addressMode: PropTypes.string,
    backFromAddAddress: PropTypes.func,
    backFromEditAddress: PropTypes.func
}

/**
 * AddressCard is a presentational React component that displays a single address entry within an address book.
 * It renders the address details, including the recipient's name, address lines, city, postal code, and country.
 * If the address is marked as preferred, a label is shown. An edit icon is provided to trigger address editing.
 * The component uses theming for styling and internationalization for localized labels.
 *
 * Props:
 * @param {Object} address - The address object containing details to display (e.g., firstName, lastName, address1, city, postalCode, countryCode, title, preferred, c_apartment).
 * @param {Function} handleEditAddress - Callback function invoked when the edit icon is clicked, receiving the address object as an argument.
 * @param {boolean} isLastAddress - Indicates if this is the last address in the list, affecting the display of the bottom border.
 *
 * The component leverages theme-based styles and internationalization hooks for consistent UI and localization.
 */
const AddressCard = ({address, handleEditAddress, isLastAddress}) => {
    /* Styling */
    const theme = useTheme()
    const {baseStyle, addressCard} = theme.components.AddressBook

    /* Utility Hooks */
    const intl = useIntl()

    return (
        <Box
            borderBottom={!isLastAddress ? '1px solid' : '0'}
            paddingBottom={!isLastAddress ? 8 : 0}
            {...addressCard.container}
        >
            <Box {...baseStyle.centerContent}>
                {address?.preferred && (
                    <Text {...addressCard.preferred}>
                        {intl.formatMessage(staticResources.preferred)}
                    </Text>
                )}
                <Box {...addressCard.wrapper}>
                    {address?.title && (
                        <Text {...addressCard.title} data-private>
                            {address?.title}
                        </Text>
                    )}
                    <Text {...addressCard.address} data-private>
                        {address.firstName} {address.lastName}
                    </Text>
                    <Text {...addressCard.address} data-private>
                        {address?.address1} {address?.c_apartment && ', ' + address?.c_apartment}
                    </Text>
                    <Text {...addressCard.address} data-private>
                        {address?.city}, {address?.postalCode},{' '}
                        {address?.countryCode === 'GB'
                            ? intl.formatMessage(staticResources.ukCountryName)
                            : address?.countryCode}
                    </Text>
                    <IconButton
                        variant="unstyled"
                        h={'auto'}
                        minW={'auto'}
                        onClick={() => handleEditAddress(address)}
                        {...addressCard.editIcon}
                        icon={<EditPencil />}
                        aria-label="Edit address"
                    />
                </Box>
            </Box>
        </Box>
    )
}

AddressCard.propTypes = {
    address: PropTypes.object,
    handleEditAddress: PropTypes.func,
    isLastAddress: PropTypes.bool
}

/**
 * AddressBook is a React functional component that provides a comprehensive user interface for managing customer addresses within an account page.
 *
 * This component allows users to view, add, edit, and remove addresses associated with their account. It leverages several hooks for state management, form handling, API interactions, and responsive design. The component dynamically renders different UI states based on the current address mode (e.g., viewing saved addresses, adding a new address, editing an existing address, or displaying an empty state when no addresses are present).
 *
 * Key Features:
 * - Displays a list of saved addresses with options to edit or delete each address.
 * - Provides forms for adding and editing addresses, with validation for mandatory fields.
 * - Handles navigation between different address modes using URL parameters and browser history.
 * - Shows loading spinners during asynchronous operations and displays success or error toasts based on API responses.
 * - Responsive design adapts the layout for mobile and desktop views.
 *
 * Dependencies:
 * - Uses custom hooks such as `useCurrentCustomer`, `useShopperCustomersMutation`, `useCustomToast`, and `useForm` for data fetching, mutations, notifications, and form state management.
 * - Integrates with theme styling and internationalization for consistent UI and localization.
 *
 * Returns:
 *   A JSX structure that conditionally renders address management forms, address cards, and appropriate UI elements based on the user's interaction and current state.
 */
const AddressBook = () => {
    /* Styling */
    const theme = useTheme()
    const {baseStyle, formStyle} = theme.components.AddressBook
    const {baseStyle: toastStyling} = theme.components.CustomToast

    /* Utility Hooks */
    const intl = useIntl()
    const isMobile = useBreakpointValue({base: true, md: false})

    const {showCustomToast} = useCustomToast()
    const form = useForm({
        mode: 'onChange',
        defaultValues: addressFormResetObject
    })
    const {data: customer, refetch, isLoading: isCustomerLoading} = useCurrentCustomer()
    const history = useHistory()
    const location = useLocation()

    /* States */
    const [addressMode, setAddressMode] = useState(null)
    const [selectedAddressId, setSelectedAddressId] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const lastSubmittedValuesRef = useRef(null)
    const globalErrorRef = useRef(null)

    /* API Hooks */
    const addCustomerAddress = useShopperCustomersMutation('createCustomerAddress')
    const updateSavedAddress = useShopperCustomersMutation('updateCustomerAddress')
    const removeCustomerAddress = useShopperCustomersMutation('removeCustomerAddress')

    /* Others */
    const {addresses, customerId} = customer

    const watchedValues = form.watch()
    const isFormValid = MANDATORY_FIELDS.every((field) => {
        const value = watchedValues[field]
        return value && value.toString().trim() !== ''
    })

    /* Navigation helper functions */
    const navigateToMode = (mode) => {
        const currentPath = location.pathname

        if (mode === 'savedaddress' || mode === 'noaddress') {
            history.push(currentPath)
        } else {
            const params = new URLSearchParams()
            params.set('mode', mode)
            history.push(`${currentPath}?${params.toString()}`)
        }
    }

    useEffect(() => {
        const urlMode = getAddressModeFromPath(location.search)

        if (urlMode) {
            setAddressMode(urlMode)
        } else {
            const defaultMode = getDefaultMode(addresses)
            setAddressMode(defaultMode)
        }
    }, [addresses, location.search])

    useEffect(() => {
        const handlePopState = () => {
            const urlMode = getAddressModeFromPath(location.search) || getDefaultMode(addresses)

            setAddressMode(urlMode)

            if (urlMode === 'savedaddress' || urlMode === 'noaddress') {
                form.reset()
                setSelectedAddressId(false)
            }
        }

        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [addresses, location.search, form])

    const globalError = form.formState.errors?.global

    useEffect(() => {
        if (!globalError || !lastSubmittedValuesRef.current) {
            return
        }

        const hasChangedSinceSubmit = Object.keys(lastSubmittedValuesRef.current).some(
            (key) => lastSubmittedValuesRef.current[key] !== watchedValues[key]
        )

        if (hasChangedSinceSubmit) {
            form.clearErrors('global')
        }
    }, [globalError, watchedValues, form])

    useEffect(() => {
        if (!globalError || !globalErrorRef.current) {
            return
        }

        globalErrorRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        })
    }, [globalError])

    const submitForm = async (address) => {
        setIsLoading(true)
        lastSubmittedValuesRef.current = form.getValues()

        const {
            addressTitle,
            firstName,
            lastName,
            address1,
            apartment,
            city,
            postalCode,
            countryCode,
            phone,
            stateCode,
            preferred
        } = address
        try {
            let data = null

            if (selectedAddressId) {
                const body = {
                    addressId: selectedAddressId,
                    title: addressTitle,
                    firstName,
                    lastName,
                    address1,
                    c_apartment: apartment,
                    city,
                    postalCode,
                    countryCode,
                    phone,
                    stateCode,
                    c_email: customer.email,
                    preferred,
                    c_isAddressBookCall: true
                }
                data = await updateSavedAddress.mutateAsync({
                    body,
                    parameters: {
                        customerId,
                        addressName: selectedAddressId
                    }
                })
                refetch()
            } else {
                // Automatically set first address as default
                const isFirstAddress = !addresses || addresses.length === 0
                const body = {
                    addressId: nanoid(),
                    title: addressTitle,
                    firstName,
                    lastName,
                    address1,
                    c_apartment: apartment,
                    city,
                    postalCode,
                    countryCode,
                    phone,
                    stateCode,
                    c_email: customer.email,
                    preferred: isFirstAddress ? true : preferred,
                    c_isAddressBookCall: true
                }
                data = await addCustomerAddress.mutateAsync({
                    body,
                    parameters: {customerId: customer.customerId}
                })
                refetch()
            }

            if (data) {
                showCustomToast({
                    message: intl.formatMessage(staticResources.successMessage),
                    styles: {...toastStyling, position: {base: 'relative', lg: 'static'}},
                    containerId: isMobile ? 'app-header' : 'account-left-container'
                })

                navigateToMode('savedaddress')
                setAddressMode('savedaddress')
                setSelectedAddressId(false)
                form.reset()
            }

            setIsLoading(false)
        } catch {
            form.setError('global', {
                type: 'manual',
                message: intl.formatMessage(staticResources.errorMessage)
            })
            setIsLoading(false)
            // GTM Modification
            sendViewErrorEvent(staticResources.errorMessage)
        }
    }

    const handleBackFromAddAddress = () => {
        const targetMode = getDefaultMode(addresses)
        navigateToMode(targetMode)
        setAddressMode(targetMode)
        setSelectedAddressId(false)
        form.reset()
    }

    const handleBackFromEditAddress = () => {
        navigateToMode('savedaddress')
        setAddressMode('savedaddress')
        setSelectedAddressId(false)
        form.reset()
    }

    const handleAddAddress = () => {
        form.reset(addressFormResetObject)
        navigateToMode('addaddress')
        setAddressMode('addaddress')
        setSelectedAddressId(false)
    }

    const handleEditAddress = (address) => {
        form.reset({
            addressTitle: address.title || '',
            address1: address.address1 || '',
            apartment: address.c_apartment || '',
            city: address.city || '',
            countryCode: address.countryCode || 'GB',
            firstName: address.firstName || '',
            lastName: address.lastName || '',
            phone: address.phone || '',
            postalCode: address.postalCode || '',
            stateCode: address.stateCode || '',
            preferred: address.preferred || false,
            email: address.c_email || ''
        })
        navigateToMode('editaddress')
        setAddressMode('editaddress')
        setSelectedAddressId(address?.addressId)
    }

    const handleRemoveAddress = async (addressId) => {
        if (!addressId) {
            form.setError('global', {
                type: 'manual',
                message: intl.formatMessage(staticResources.errorMessage)
            })
            // GTM Modification
            sendViewErrorEvent(staticResources.errorMessage)
            return
        }

        setIsLoading(true)

        try {
            await removeCustomerAddress.mutateAsync(
                {
                    parameters: {
                        customerId,
                        addressName: addressId
                    }
                },
                {
                    onSuccess: () => {
                        navigateToMode('savedaddress')
                        setAddressMode('savedaddress')
                        setSelectedAddressId(false)
                        form.reset()
                        showCustomToast({
                            message: intl.formatMessage(staticResources.successMessage),
                            styles: {...toastStyling, position: {base: 'relative', lg: 'static'}},
                            containerId: isMobile ? 'app-header' : 'account-left-container'
                        })
                        setIsLoading(false)
                    },
                    onError: () => {
                        form.setError('global', {
                            type: 'manual',
                            message: intl.formatMessage(staticResources.errorMessage)
                        })
                        setIsLoading(false)
                        // GTM Modification
                        sendViewErrorEvent(staticResources.errorMessage)
                    }
                }
            )
        } catch {
            form.setError('global', {
                type: 'manual',
                message: intl.formatMessage(staticResources.errorMessage)
            })
            // GTM Modification
            sendViewErrorEvent(staticResources.errorMessage)
            setIsLoading(false)
        }
    }

    return (
        <Stack data-testid="account-address-book" {...baseStyle.container}>
            <HeadingAddressBook
                key="account-address-heading"
                addressMode={addressMode}
                backFromAddAddress={handleBackFromAddAddress}
                backFromEditAddress={handleBackFromEditAddress}
            />
            {addressMode === 'noaddress' && (
                <Flex
                    {...baseStyle.addressWrapper}

                    {...baseStyle.centerContent}
                >
                    <Box>
                        <Text {...baseStyle.noAddressTitle}>
                            {intl.formatMessage(staticResources.noAddressTitle)}
                        </Text>
                        <Text {...baseStyle.noAddressSubTitle}>
                            {intl.formatMessage(staticResources.noAddressSubTitle)}
                        </Text>
                    </Box>
                    <Button
                        onClick={handleAddAddress}
                        {...baseStyle.cta}
                        {...baseStyle.addAddressCta}
                    >
                        {intl.formatMessage(staticResources.addAddressLabel)}
                    </Button>
                </Flex>
            )}
            {(addressMode === 'addaddress' || addressMode === 'editaddress') && (
                <form
                    onSubmit={form.handleSubmit(submitForm)}
                    style={{height: '100%', display: 'flex', flexDirection: 'column'}}
                >
                    <Box
                        {...formStyle.container}

                        {...baseStyle.centerContent}
                    >
                        <Stack spacing={4}>
                            {form.formState.errors?.global && (
                                <Box ref={globalErrorRef} {...formStyle.errorContainer}>
                                    <Alert variant={'custom'} status="error">
                                        <Text>{form.formState.errors.global.message}</Text>
                                    </Alert>
                                </Box>
                            )}
                            <AddressBookFields form={form} />
                        </Stack>
                        <Box>
                            <Button
                                type="submit"
                                title="Submit"
                                isDisabled={!isFormValid}
                                {...baseStyle.cta}
                                {...formStyle.submitCta}
                            >
                                {addressMode === 'addaddress'
                                    ? intl.formatMessage(staticResources.addAddressLabel)
                                    : intl.formatMessage(staticResources.updatedAddressLabel)}
                            </Button>
                            {addressMode === 'editaddress' && (
                                <Box {...formStyle.deleteCtaContainer}>
                                    <Button
                                        type="button"
                                        variant={'link'}
                                        onClick={() => handleRemoveAddress(selectedAddressId)}
                                    >
                                        {intl.formatMessage(staticResources.deleteAddressLabel)}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </form>
            )}
            {addressMode === 'savedaddress' && (
                <Flex {...baseStyle.addressWrapper}>
                    <Box>
                        {addresses?.map((address, index) => {
                            return (
                                <AddressCard
                                    key={address.addressId}
                                    address={address}
                                    handleEditAddress={() => handleEditAddress(address)}
                                    isLastAddress={addresses?.length - 1 === index}
                                />
                            )
                        })}
                    </Box>
                    <Button
                        onClick={handleAddAddress}
                        {...baseStyle.cta}

                        {...baseStyle.centerContent}
                    >
                        {intl.formatMessage(staticResources.addAddressLabel)}
                    </Button>
                </Flex>
            )}
            {(isLoading || isCustomerLoading) && <LoadingSpinner />}
        </Stack>
    )
}

AddressBook.getTemplateName = () => 'account-address-book'

export default AddressBook
